import {
  PublicEvent as PublicEventSchema,
  RoomSettings as RoomSettingsSchema,
  ServerMessage as ServerMessageSchema,
  ViewerSnapshot as ViewerSnapshotSchema,
  type BotDifficulty,
  type ClientAction,
  type PublicEvent,
  type RoomSettings,
  type ViewerSnapshot,
} from "@guandan/protocol";
import { getSession } from "./session";
import {
  RemoteSpectatorAuthority,
  type SpectatorAccessInput,
} from "../game/spectator-authority";

export type PlatformMode = "remote" | "local";

export interface PlatformIdentity {
  readonly userId: string;
  readonly email: string;
  readonly nickname: string;
}

export interface RoomMemberView {
  readonly userId: string;
  readonly nickname: string;
  readonly seat: 0 | 1 | 2 | 3;
  readonly role: "owner" | "player";
  readonly ready: boolean;
  readonly connected: boolean;
}

export interface RoomSeatView {
  readonly userId: string;
  readonly nickname: string;
  readonly seat: 0 | 1 | 2 | 3;
  readonly isBot: boolean;
  readonly connected: boolean;
}

export interface PublicRoomView {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly status: "waiting" | "playing" | "settled" | "closed";
  readonly humanCount: number;
  readonly capacity: 4;
  readonly passwordProtected: boolean;
  readonly allowBots: boolean;
  readonly allowSpectators: boolean;
  readonly botDifficulty: BotDifficulty;
  readonly createdAt: number;
}

export interface LobbyRoom {
  readonly id: string;
  readonly code: string;
  readonly ownerId: string;
  readonly status: "waiting" | "playing" | "settled" | "closed";
  readonly settings: RoomSettings;
  readonly members: readonly RoomMemberView[];
  readonly players: readonly RoomSeatView[];
  readonly selfMember: RoomMemberView;
  readonly version: number;
  readonly phase: ViewerSnapshot["phase"];
  readonly inviteToken?: string;
  /** Server-authored relative invitation data, validated before it is rendered as a QR code. */
  readonly qrData?: string;
}

export interface MatchmakingView {
  readonly id: string;
  readonly status: "queued" | "matched" | "cancelled" | "expired";
  readonly queuedAt: number;
  readonly matchedRoomId: string | null;
  readonly searchRange: number;
  readonly humanPriorityRemainingMs: number;
  readonly timeoutRemainingMs: number;
}

export type ActiveRoomView = Pick<
  LobbyRoom,
  "id" | "code" | "status" | "settings"
>;

export interface RoomJoinInput {
  readonly code?: string;
  readonly roomId?: string;
  readonly inviteToken?: string;
  readonly password?: string;
}

export interface PlatformService {
  readonly mode: PlatformMode;
  readonly identity: PlatformIdentity;
  /** Null for the honest local/offline fallback; only the remote service can spectate. */
  readonly spectator: SpectatorGateway | null;
  createRoom(settings: RoomSettings, password?: string): Promise<LobbyRoom>;
  joinRoom(input: RoomJoinInput): Promise<LobbyRoom>;
  listPublicRooms(): Promise<readonly PublicRoomView[]>;
  refreshRoom(roomId: string): Promise<LobbyRoom>;
  setReady(room: LobbyRoom, ready: boolean): Promise<LobbyRoom>;
  startRoom(room: LobbyRoom): Promise<LobbyRoom>;
  addBot(
    room: LobbyRoom,
    seat: 0 | 1 | 2 | 3,
    difficulty: BotDifficulty,
  ): Promise<LobbyRoom>;
  removeBot(room: LobbyRoom, seat: 0 | 1 | 2 | 3): Promise<LobbyRoom>;
  leaveRoom(roomId: string): Promise<void>;
  endRoom(roomId: string): Promise<void>;
  activeRoom(): Promise<ActiveRoomView | null>;
  rematchRoom(sourceRoomId: string, requestId: string): Promise<LobbyRoom>;
  enqueueMatch(input: {
    mode: "ranked" | "casual";
    rating: number;
    idempotencyKey: string;
  }): Promise<MatchmakingView>;
  matchStatus(): Promise<MatchmakingView | null>;
  cancelMatch(): Promise<MatchmakingView | null>;
}

export interface SpectatorGateway {
  open(input: SpectatorAccessInput): RemoteSpectatorAuthority;
}

interface ApiErrorBody {
  readonly error?: {
    readonly code?: unknown;
    readonly message?: unknown;
    readonly retryAfterSeconds?: unknown;
  };
}

export class PlatformApiError extends Error {
  constructor(
    message: string,
    readonly code = "UNKNOWN",
    readonly status = 0,
    readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "PlatformApiError";
  }
}

interface RemoteMember {
  readonly userId: string;
  readonly nickname: string;
  readonly seat: number;
  readonly role: string;
  readonly ready: boolean;
  readonly connected: boolean;
}

interface RemoteRoomRecord {
  readonly id: string;
  readonly code: string;
  readonly ownerId: string;
  readonly status: string;
  readonly settings: unknown;
}

interface RemoteRoomEnvelope {
  readonly room: RemoteRoomRecord;
  readonly members: readonly RemoteMember[];
}

interface RemoteCreatedEnvelope {
  readonly room: RemoteRoomRecord;
  readonly member: RemoteMember;
  readonly inviteToken: unknown;
  readonly qrData: unknown;
}

interface RemoteRematchEnvelope {
  readonly sourceRoomId: unknown;
  readonly roomId: unknown;
  readonly code: unknown;
  readonly room: unknown;
  readonly member: unknown;
  readonly members: unknown;
  readonly inviteToken: unknown;
  readonly invitePath: unknown;
  readonly inviteUrl: unknown;
  readonly qrData: unknown;
}

type RoomControlIntent =
  | { readonly type: "READY"; readonly ready: boolean }
  | { readonly type: "START_GAME" }
  | {
      readonly type: "ADD_BOT";
      readonly seat: 0 | 1 | 2 | 3;
      readonly difficulty: BotDifficulty;
    }
  | { readonly type: "REMOVE_BOT"; readonly seat: 0 | 1 | 2 | 3 };

const REQUEST_TIMEOUT_MS = 8_000;
const LOCAL_KEY = "tongxin.guandan.platform.v1";

export class RemotePlatformService implements PlatformService {
  readonly mode = "remote" as const;
  readonly spectator: SpectatorGateway;

  constructor(
    private readonly baseUrl: string,
    private readonly token: string,
    readonly identity: PlatformIdentity,
    private readonly fetcher: typeof fetch = fetch,
    websocketBaseUrl = apiToWebSocketUrl(baseUrl),
  ) {
    this.spectator = Object.freeze({
      open: (input: SpectatorAccessInput) =>
        new RemoteSpectatorAuthority(
          websocketBaseUrl,
          this.baseUrl,
          input,
          this.token,
          { fetch: this.fetcher },
        ),
    });
  }

  async createRoom(
    settings: RoomSettings,
    password?: string,
  ): Promise<LobbyRoom> {
    const body = await this.request<RemoteCreatedEnvelope>("/rooms", {
      method: "POST",
      body: JSON.stringify({ settings, ...(password ? { password } : {}) }),
    });
    const room = parseRoom(body.room);
    const member = parseMember(body.member);
    const inviteToken = requireString(body.inviteToken, "邀请令牌");
    return {
      ...room,
      members: [member],
      players: [memberToSeat(member)],
      selfMember: member,
      version: 0,
      phase: "waiting",
      inviteToken,
      qrData: parseInviteQrData(body.qrData, room.code, inviteToken),
    };
  }

  async joinRoom(input: RoomJoinInput): Promise<LobbyRoom> {
    const body = await this.request<{
      room: RemoteRoomRecord;
      member: RemoteMember;
    }>(
      "/rooms/join",
      {
        method: "POST",
        body: JSON.stringify(input),
      },
      true,
    );
    return this.refreshRoom(parseRoom(body.room).id);
  }

  async listPublicRooms(): Promise<readonly PublicRoomView[]> {
    const body = await this.request<{ rooms?: unknown }>("/rooms", {}, true);
    if (!Array.isArray(body.rooms))
      throw new PlatformApiError("房间列表响应无效", "INVALID_RESPONSE");
    return body.rooms.map(parsePublicRoom);
  }

  async refreshRoom(roomId: string): Promise<LobbyRoom> {
    const encoded = encodeURIComponent(roomId);
    const [detail, eventsBody, snapshotBody] = await Promise.all([
      this.request<RemoteRoomEnvelope>(`/rooms/${encoded}`, {}, true),
      this.request<{ events?: unknown }>(
        `/rooms/${encoded}/events?after=-1`,
        {},
        true,
      ),
      this.request<{ snapshot?: unknown }>(
        `/rooms/${encoded}/snapshot`,
        {},
        true,
      ),
    ]);
    const snapshot = ViewerSnapshotSchema.parse(snapshotBody.snapshot);
    const parsedEvents = PublicEventSchema.array().safeParse(eventsBody.events);
    if (!parsedEvents.success)
      throw new PlatformApiError("房间事件响应无效", "INVALID_RESPONSE");
    return mergeRoom(detail, snapshot, parsedEvents.data, this.identity.userId);
  }

  setReady(room: LobbyRoom, ready: boolean): Promise<LobbyRoom> {
    return this.roomAction(room, { type: "READY", ready }, true);
  }

  startRoom(room: LobbyRoom): Promise<LobbyRoom> {
    return this.roomAction(room, { type: "START_GAME" }, true);
  }

  addBot(
    room: LobbyRoom,
    seat: 0 | 1 | 2 | 3,
    difficulty: BotDifficulty,
  ): Promise<LobbyRoom> {
    return this.roomAction(room, { type: "ADD_BOT", seat, difficulty }, true);
  }

  removeBot(room: LobbyRoom, seat: 0 | 1 | 2 | 3): Promise<LobbyRoom> {
    return this.roomAction(room, { type: "REMOVE_BOT", seat }, true);
  }

  async leaveRoom(roomId: string): Promise<void> {
    try {
      await this.request(
        `/rooms/${encodeURIComponent(roomId)}`,
        { method: "DELETE" },
        false,
        true,
      );
    } catch (error) {
      if (
        error instanceof PlatformApiError &&
        (error.status === 403 || error.status === 404)
      )
        return;
      throw error;
    }
  }

  async endRoom(roomId: string): Promise<void> {
    await this.request(
      `/rooms/${encodeURIComponent(roomId)}/end`,
      { method: "POST" },
      false,
      true,
    );
  }

  async activeRoom(): Promise<ActiveRoomView | null> {
    const body = await this.request<{ room?: RemoteRoomRecord | null }>(
      "/me/active-room",
      {},
      true,
    );
    return body.room === null || body.room === undefined
      ? null
      : parseRoom(body.room);
  }

  async rematchRoom(
    sourceRoomId: string,
    requestId: string,
  ): Promise<LobbyRoom> {
    const body = await this.request(
      `/rooms/${encodeURIComponent(sourceRoomId)}/rematch`,
      {
        method: "POST",
        headers: { "idempotency-key": requestId },
        body: JSON.stringify({ requestId }),
      },
      true,
    );
    const parsed = parseRematchEnvelope(
      body,
      sourceRoomId,
      this.identity.userId,
    );
    const room = await this.refreshRoom(parsed.room.id);
    if (
      room.status !== "waiting" ||
      room.id !== parsed.room.id ||
      room.code !== parsed.room.code
    ) {
      throw new PlatformApiError(
        "续局等待房状态与创建响应不一致",
        "INVALID_RESPONSE",
      );
    }
    return { ...room, inviteToken: parsed.inviteToken, qrData: parsed.qrData };
  }

  enqueueMatch(input: {
    mode: "ranked" | "casual";
    rating: number;
    idempotencyKey: string;
  }): Promise<MatchmakingView> {
    return this.request(
      "/matchmaking",
      { method: "POST", body: JSON.stringify(input) },
      true,
    ).then(parseMatchmaking);
  }

  async matchStatus(): Promise<MatchmakingView | null> {
    const body = await this.request<{ entry?: unknown }>(
      "/matchmaking",
      {},
      true,
    );
    return body.entry === null || body.entry === undefined
      ? null
      : parseMatchmaking(body.entry);
  }

  async cancelMatch(): Promise<MatchmakingView | null> {
    try {
      return parseMatchmaking(
        await this.request("/matchmaking", { method: "DELETE" }, true),
      );
    } catch (error) {
      if (error instanceof PlatformApiError && error.status === 404)
        return null;
      throw error;
    }
  }

  private async roomAction(
    room: LobbyRoom,
    intent: RoomControlIntent,
    retryStale: boolean,
  ): Promise<LobbyRoom> {
    const action: ClientAction = {
      ...intent,
      actionId: crypto.randomUUID(),
      expectedVersion: room.version,
      roomId: room.id,
    };
    const body = await this.request<{ messages?: unknown }>(
      `/rooms/${encodeURIComponent(room.id)}/actions`,
      {
        method: "POST",
        body: JSON.stringify(action),
      },
      true,
    );
    const parsed = ServerMessageSchema.array().safeParse(body.messages);
    if (!parsed.success)
      throw new PlatformApiError("牌桌动作响应无效", "INVALID_RESPONSE");
    const failure = parsed.data.find((message) => message.type === "ERROR");
    if (failure) {
      if (retryStale && failure.code === "STALE_VERSION") {
        const current = await this.refreshRoom(room.id);
        return this.roomAction(current, intent, false);
      }
      throw new PlatformApiError(
        failure.message,
        failure.code,
        failure.code === "FORBIDDEN" ? 403 : 409,
      );
    }
    return this.refreshRoom(room.id);
  }

  private async request<T = unknown>(
    path: string,
    init: RequestInit = {},
    retryable = false,
    empty = false,
  ): Promise<T> {
    const attempts = retryable ? 2 : 1;
    let lastError: unknown;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const headers = new Headers(init.headers);
        headers.set("authorization", `Bearer ${this.token}`);
        if (init.body && !headers.has("content-type"))
          headers.set("content-type", "application/json");
        const response = await this.fetcher.call(
          globalThis,
          `${this.baseUrl.replace(/\/$/u, "")}${path}`,
          {
            ...init,
            signal: controller.signal,
            headers,
          },
        );
        if (!response.ok) throw await responseError(response);
        if (empty || response.status === 204) return undefined as T;
        return (await response.json()) as T;
      } catch (error) {
        lastError = error;
        if (attempt + 1 < attempts && retryableFailure(error)) continue;
        if (isAbortError(error))
          throw new PlatformApiError("服务响应超时，请重试", "TIMEOUT");
        if (error instanceof PlatformApiError) throw error;
        throw new PlatformApiError(
          "无法连接游戏服务，请检查网络后重试",
          "NETWORK",
        );
      } finally {
        clearTimeout(timer);
      }
    }
    throw lastError instanceof Error
      ? lastError
      : new PlatformApiError("请求失败", "UNKNOWN");
  }
}

interface LocalRoomRecord {
  readonly id: string;
  readonly code: string;
  readonly ownerId: string;
  status: LobbyRoom["status"];
  readonly settings: RoomSettings;
  readonly passwordHash: string | null;
  members: RoomMemberView[];
  bots?: { readonly seat: 0 | 1 | 2 | 3; readonly difficulty: BotDifficulty }[];
  readonly createdAt: number;
  version: number;
}

interface LocalPlatformState {
  readonly rooms: LocalRoomRecord[];
}

export class LocalPlatformService implements PlatformService {
  readonly mode = "local" as const;
  readonly spectator = null;
  private match: MatchmakingView | null = null;

  constructor(
    readonly identity: PlatformIdentity,
    private readonly storage: Storage = localStorage,
  ) {}

  async createRoom(
    settingsInput: RoomSettings,
    password?: string,
  ): Promise<LobbyRoom> {
    const settings = RoomSettingsSchema.parse({
      ...settingsInput,
      passwordProtected: Boolean(password),
    });
    const state = this.read();
    const active = state.rooms.find(
      (room) =>
        room.status === "waiting" &&
        room.members.some((member) => member.userId === this.identity.userId),
    );
    if (active) return this.toLobby(active);
    const member: RoomMemberView = {
      ...this.identity,
      seat: 0,
      role: "owner",
      ready: false,
      connected: true,
    };
    const code = this.uniqueCode(state.rooms);
    const room: LocalRoomRecord = {
      id: `local-friend-${crypto.randomUUID()}`,
      code,
      ownerId: this.identity.userId,
      status: "waiting",
      settings,
      passwordHash: password ? await localPasswordDigest(code, password) : null,
      members: [member],
      bots: [],
      createdAt: Date.now(),
      version: 0,
    };
    this.write({ rooms: [room, ...state.rooms].slice(0, 40) });
    const inviteToken = `local-${room.code}`;
    return {
      ...this.toLobby(room),
      inviteToken,
      qrData: `/?room=${encodeURIComponent(room.code)}&invite=${encodeURIComponent(inviteToken)}`,
    };
  }

  async joinRoom(input: RoomJoinInput): Promise<LobbyRoom> {
    const state = this.read();
    const room = state.rooms.find(
      (candidate) =>
        candidate.id === input.roomId ||
        candidate.code === input.code ||
        input.inviteToken === `local-${candidate.code}`,
    );
    if (!room || room.status !== "waiting")
      throw new PlatformApiError("本地房间不存在或已开局", "NOT_FOUND", 404);
    if (
      room.settings.passwordProtected &&
      (!room.passwordHash ||
        !input.password ||
        (await localPasswordDigest(room.code, input.password)) !==
          room.passwordHash)
    )
      throw new PlatformApiError("房间密码错误", "FORBIDDEN", 403);
    let member = room.members.find(
      (candidate) => candidate.userId === this.identity.userId,
    );
    if (!member) {
      if (room.members.length >= 4)
        throw new PlatformApiError("房间已满", "ROOM_FULL", 409);
      const occupied = new Set(room.members.map((candidate) => candidate.seat));
      const seat = ([0, 1, 2, 3] as const).find(
        (candidate) => !occupied.has(candidate),
      );
      if (seat === undefined)
        throw new PlatformApiError("房间已满", "ROOM_FULL", 409);
      room.bots = (room.bots ?? []).filter((bot) => bot.seat !== seat);
      member = {
        ...this.identity,
        seat,
        role: "player",
        ready: false,
        connected: true,
      };
      room.members.push(member);
      room.version += 1;
      this.write(state);
    }
    return this.toLobby(room);
  }

  listPublicRooms(): Promise<readonly PublicRoomView[]> {
    const rooms = this.read()
      .rooms.filter(
        (room) =>
          room.settings.visibility === "public" && room.status === "waiting",
      )
      .map((room) => ({
        id: room.id,
        code: room.code,
        name: room.settings.name,
        status: room.status,
        humanCount: room.members.length,
        capacity: 4 as const,
        passwordProtected: room.settings.passwordProtected,
        allowBots: room.settings.allowBots,
        allowSpectators: room.settings.allowSpectators,
        botDifficulty: room.settings.botDifficulty,
        createdAt: room.createdAt,
      }));
    return Promise.resolve(rooms);
  }

  refreshRoom(roomId: string): Promise<LobbyRoom> {
    const room = this.read().rooms.find((candidate) => candidate.id === roomId);
    return room
      ? Promise.resolve(this.toLobby(room))
      : Promise.reject(
          new PlatformApiError("本地房间不存在", "NOT_FOUND", 404),
        );
  }

  setReady(lobby: LobbyRoom, ready: boolean): Promise<LobbyRoom> {
    const state = this.read();
    const room = this.requireLocalRoom(state, lobby.id);
    const member = room.members.find(
      (candidate) => candidate.userId === this.identity.userId,
    );
    if (!member)
      return Promise.reject(
        new PlatformApiError("你不在该房间", "FORBIDDEN", 403),
      );
    room.members = room.members.map((candidate) =>
      candidate.userId === member.userId ? { ...candidate, ready } : candidate,
    );
    room.version += 1;
    this.write(state);
    return Promise.resolve(this.toLobby(room));
  }

  startRoom(lobby: LobbyRoom): Promise<LobbyRoom> {
    const state = this.read();
    const room = this.requireLocalRoom(state, lobby.id);
    if (room.ownerId !== this.identity.userId)
      return Promise.reject(
        new PlatformApiError("仅房主可开局", "FORBIDDEN", 403),
      );
    if (!room.members.every((member) => member.ready))
      return Promise.reject(
        new PlatformApiError("仍有真人玩家未准备", "CONFLICT", 409),
      );
    const occupied = new Set(room.members.map((member) => member.seat));
    if (!room.settings.allowBots && occupied.size < 4)
      return Promise.reject(
        new PlatformApiError("本房间需四名真人到齐", "CONFLICT", 409),
      );
    if (room.settings.allowBots) {
      const bots = room.bots ?? [];
      for (const seat of [0, 1, 2, 3] as const) {
        if (!occupied.has(seat) && !bots.some((bot) => bot.seat === seat))
          bots.push({ seat, difficulty: room.settings.botDifficulty });
      }
      room.bots = bots;
    }
    room.status = "playing";
    room.version += 1;
    this.write(state);
    return Promise.resolve(this.toLobby(room));
  }

  addBot(
    lobby: LobbyRoom,
    seat: 0 | 1 | 2 | 3,
    difficulty: BotDifficulty,
  ): Promise<LobbyRoom> {
    const state = this.read();
    const room = this.requireLocalRoom(state, lobby.id);
    if (room.ownerId !== this.identity.userId)
      return Promise.reject(
        new PlatformApiError("仅房主可添加电脑", "FORBIDDEN", 403),
      );
    if (room.status !== "waiting")
      return Promise.reject(
        new PlatformApiError("只能在等待阶段加入电脑", "CONFLICT", 409),
      );
    if (!room.settings.allowBots)
      return Promise.reject(
        new PlatformApiError("本房间未启用电脑玩家", "FORBIDDEN", 403),
      );
    const bots = room.bots ?? [];
    if (
      room.members.some((member) => member.seat === seat) ||
      bots.some((bot) => bot.seat === seat)
    ) {
      return Promise.reject(
        new PlatformApiError("座位已占用", "CONFLICT", 409),
      );
    }
    room.bots = [...bots, { seat, difficulty }];
    room.version += 1;
    this.write(state);
    return Promise.resolve(this.toLobby(room));
  }

  removeBot(lobby: LobbyRoom, seat: 0 | 1 | 2 | 3): Promise<LobbyRoom> {
    const state = this.read();
    const room = this.requireLocalRoom(state, lobby.id);
    if (room.ownerId !== this.identity.userId)
      return Promise.reject(
        new PlatformApiError("仅房主可移除电脑", "FORBIDDEN", 403),
      );
    if (room.status !== "waiting")
      return Promise.reject(
        new PlatformApiError("只能在等待阶段移除电脑", "CONFLICT", 409),
      );
    const bots = room.bots ?? [];
    if (!bots.some((bot) => bot.seat === seat))
      return Promise.reject(
        new PlatformApiError("该座位不是电脑", "INVALID_REQUEST", 400),
      );
    room.bots = bots.filter((bot) => bot.seat !== seat);
    room.version += 1;
    this.write(state);
    return Promise.resolve(this.toLobby(room));
  }

  leaveRoom(roomId: string): Promise<void> {
    const state = this.read();
    const room = state.rooms.find((candidate) => candidate.id === roomId);
    if (!room || room.status === "playing") return Promise.resolve();
    const rooms =
      room.ownerId === this.identity.userId
        ? state.rooms.filter((candidate) => candidate.id !== roomId)
        : state.rooms.map((candidate) =>
            candidate.id === roomId
              ? {
                  ...candidate,
                  members: candidate.members.filter(
                    (member) => member.userId !== this.identity.userId,
                  ),
                }
              : candidate,
          );
    this.write({ rooms });
    return Promise.resolve();
  }

  endRoom(roomId: string): Promise<void> {
    const state = this.read();
    const room = state.rooms.find((candidate) => candidate.id === roomId);
    if (!room) return Promise.resolve();
    const rooms =
      room.ownerId === this.identity.userId
        ? state.rooms.filter((candidate) => candidate.id !== roomId)
        : state.rooms.map((candidate) =>
            candidate.id === roomId
              ? {
                  ...candidate,
                  members: candidate.members.filter(
                    (member) => member.userId !== this.identity.userId,
                  ),
                }
              : candidate,
          );
    this.write({ rooms });
    return Promise.resolve();
  }

  activeRoom(): Promise<ActiveRoomView | null> {
    const room = this.read().rooms.find(
      (candidate) =>
        (candidate.status === "waiting" || candidate.status === "playing") &&
        candidate.members.some(
          (member) => member.userId === this.identity.userId,
        ),
    );
    if (!room) return Promise.resolve(null);
    const { id, code, status, settings: roomSettings } = room;
    return Promise.resolve({ id, code, status, settings: roomSettings });
  }

  rematchRoom(sourceRoomId: string, requestId: string): Promise<LobbyRoom> {
    void sourceRoomId;
    void requestId;
    return Promise.reject(
      new PlatformApiError(
        "原班续局必须连接权威房间服务，请恢复网络后重试",
        "UNAVAILABLE",
        503,
      ),
    );
  }

  enqueueMatch(input: {
    mode: "ranked" | "casual";
    rating: number;
    idempotencyKey: string;
  }): Promise<MatchmakingView> {
    if (
      this.match?.status === "queued" ||
      this.match?.id === input.idempotencyKey
    )
      return Promise.resolve(this.match);
    this.match = {
      id: input.idempotencyKey,
      status: "queued",
      queuedAt: Date.now(),
      matchedRoomId: null,
      searchRange: 0,
      humanPriorityRemainingMs: 0,
      timeoutRemainingMs: 1_200,
    };
    return Promise.resolve(this.match);
  }

  matchStatus(): Promise<MatchmakingView | null> {
    if (this.match?.status === "queued") {
      const remaining = Math.max(0, 1_200 - (Date.now() - this.match.queuedAt));
      this.match =
        remaining > 0
          ? { ...this.match, timeoutRemainingMs: remaining }
          : {
              ...this.match,
              status: "matched",
              matchedRoomId: `local-match-${crypto.randomUUID()}`,
              timeoutRemainingMs: 0,
            };
    }
    return Promise.resolve(this.match);
  }

  cancelMatch(): Promise<MatchmakingView | null> {
    if (this.match?.status === "queued")
      this.match = { ...this.match, status: "cancelled" };
    return Promise.resolve(this.match);
  }

  private toLobby(room: LocalRoomRecord): LobbyRoom {
    const selfMember = room.members.find(
      (member) => member.userId === this.identity.userId,
    );
    if (!selfMember)
      throw new PlatformApiError("你不在该房间", "FORBIDDEN", 403);
    return {
      id: room.id,
      code: room.code,
      ownerId: room.ownerId,
      status: room.status,
      settings: room.settings,
      members: room.members,
      players: [
        ...room.members.map(memberToSeat),
        ...(room.bots ?? []).map((bot) => ({
          userId: `local-bot-${bot.seat}`,
          nickname: `${difficultyLabel(bot.difficulty)} AI`,
          seat: bot.seat,
          isBot: true,
          connected: true,
        })),
      ].sort((left, right) => left.seat - right.seat),
      selfMember,
      version: room.version,
      phase:
        room.status === "playing"
          ? "playing"
          : room.status === "settled"
            ? "settled"
            : "waiting",
    };
  }

  private requireLocalRoom(
    state: LocalPlatformState,
    roomId: string,
  ): LocalRoomRecord {
    const room = state.rooms.find((candidate) => candidate.id === roomId);
    if (!room) throw new PlatformApiError("本地房间不存在", "NOT_FOUND", 404);
    return room;
  }

  private uniqueCode(rooms: readonly LocalRoomRecord[]): string {
    for (let attempt = 0; attempt < 20; attempt += 1) {
      const values = new Uint32Array(1);
      crypto.getRandomValues(values);
      const code = String(100_000 + ((values[0] ?? 0) % 900_000));
      if (!rooms.some((room) => room.code === code)) return code;
    }
    throw new PlatformApiError("暂时无法生成本地房号", "CONFLICT");
  }

  private read(): LocalPlatformState {
    try {
      const raw = this.storage.getItem(LOCAL_KEY);
      if (!raw) return { rooms: [] };
      const parsed = JSON.parse(raw) as Partial<LocalPlatformState>;
      return { rooms: Array.isArray(parsed.rooms) ? parsed.rooms : [] };
    } catch {
      return { rooms: [] };
    }
  }

  private write(state: LocalPlatformState): void {
    this.storage.setItem(LOCAL_KEY, JSON.stringify(state));
  }
}

export function createPlatformService(profile: {
  readonly email: string;
  readonly nickname: string;
}): PlatformService {
  const session = getSession();
  const configured = import.meta.env.VITE_GAME_API_URL as string | undefined;
  const websocketConfigured = import.meta.env.VITE_GAME_WS_URL as
    string | undefined;
  if (configured && websocketConfigured && session) {
    return new RemotePlatformService(
      configured,
      session.token,
      {
        userId: session.userId,
        email: session.email,
        nickname: profile.nickname,
      },
      fetch,
      websocketConfigured,
    );
  }
  return new LocalPlatformService({
    userId: `local:${profile.email.toLowerCase()}`,
    email: profile.email,
    nickname: profile.nickname,
  });
}

function mergeRoom(
  detail: RemoteRoomEnvelope,
  snapshot: ViewerSnapshot,
  events: readonly PublicEvent[],
  selfUserId: string,
): LobbyRoom {
  const room = parseRoom(detail.room);
  const readiness = new Map<number, boolean>();
  for (const member of detail.members) readiness.set(member.seat, member.ready);
  for (const event of events) {
    if (event.type !== "PLAYER_READY") continue;
    const seat = event.payload.seat;
    const ready = event.payload.ready;
    if (typeof seat === "number" && typeof ready === "boolean")
      readiness.set(seat, ready);
  }
  const members = detail.members.map((member) =>
    parseMember({ ...member, ready: readiness.get(member.seat) ?? false }),
  );
  const selfMember = members.find((member) => member.userId === selfUserId);
  if (!selfMember)
    throw new PlatformApiError("登录用户不在该房间", "FORBIDDEN", 403);
  const players: RoomSeatView[] = snapshot.players.map((player) => ({
    userId: player.userId,
    nickname: player.nickname,
    seat: player.seat as 0 | 1 | 2 | 3,
    isBot: player.isBot,
    connected: player.connected,
  }));
  return {
    ...room,
    members,
    players,
    selfMember,
    version: snapshot.version,
    phase: snapshot.phase,
  };
}

function memberToSeat(member: RoomMemberView): RoomSeatView {
  return {
    userId: member.userId,
    nickname: member.nickname,
    seat: member.seat,
    isBot: false,
    connected: member.connected,
  };
}

function difficultyLabel(difficulty: BotDifficulty): string {
  if (difficulty === "master") return "大师";
  if (difficulty === "expert") return "高手";
  return "标准";
}

function parseInviteQrData(
  value: unknown,
  roomCode: string,
  inviteToken: string,
): string {
  const raw = requireString(value, "二维码数据");
  let parsed: URL;
  try {
    parsed = new URL(raw, "https://invite.invalid");
  } catch {
    throw new PlatformApiError("二维码数据无效", "INVALID_RESPONSE");
  }
  const keys = [...parsed.searchParams.keys()];
  const supportedKeys = new Set(["room", "invite"]);
  const hasOnlySupportedKeys = keys.every((key) => supportedKeys.has(key));
  if (
    !raw.startsWith("/?") ||
    parsed.origin !== "https://invite.invalid" ||
    parsed.username !== "" ||
    parsed.password !== "" ||
    parsed.pathname !== "/" ||
    parsed.hash !== "" ||
    !hasOnlySupportedKeys ||
    parsed.searchParams.getAll("room").length !== 1 ||
    parsed.searchParams.getAll("invite").length !== 1 ||
    parsed.searchParams.get("room") !== roomCode ||
    parsed.searchParams.get("invite") !== inviteToken
  ) {
    throw new PlatformApiError(
      "二维码邀请数据与房间签名不一致",
      "INVALID_RESPONSE",
    );
  }
  return `/?room=${encodeURIComponent(roomCode)}&invite=${encodeURIComponent(inviteToken)}`;
}

function parseRematchEnvelope(
  value: unknown,
  sourceRoomId: string,
  selfUserId: string,
): {
  room: ReturnType<typeof parseRoom>;
  inviteToken: string;
  qrData: string;
} {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new PlatformApiError("续局响应无效", "INVALID_RESPONSE");
  const envelope = value as unknown as RemoteRematchEnvelope;
  const expectedKeys = [
    "code",
    "invitePath",
    "inviteToken",
    "inviteUrl",
    "member",
    "members",
    "qrData",
    "room",
    "roomId",
    "sourceRoomId",
  ];
  if (Object.keys(value).sort().join("\0") !== expectedKeys.sort().join("\0"))
    throw new PlatformApiError("续局响应字段无效", "INVALID_RESPONSE");
  if (
    !envelope.room ||
    typeof envelope.room !== "object" ||
    Array.isArray(envelope.room)
  )
    throw new PlatformApiError("续局房间响应无效", "INVALID_RESPONSE");
  requireExactObjectKeys(
    envelope.room,
    ["code", "id", "ownerId", "settings", "status"],
    "续局房间响应字段无效",
  );
  let room: ReturnType<typeof parseRoom>;
  try {
    room = parseRoom(envelope.room as unknown as RemoteRoomRecord);
  } catch (error) {
    if (error instanceof PlatformApiError) throw error;
    throw new PlatformApiError("续局房间配置无效", "INVALID_RESPONSE");
  }
  const responseSourceRoomId = requireString(envelope.sourceRoomId, "原房 ID");
  const roomId = requireString(envelope.roomId, "续局房间 ID");
  const code = requireString(envelope.code, "续局房号");
  if (
    responseSourceRoomId !== sourceRoomId ||
    room.id === sourceRoomId ||
    room.id !== roomId ||
    room.code !== code ||
    room.status !== "waiting"
  ) {
    throw new PlatformApiError("续局房间标识或状态无效", "INVALID_RESPONSE");
  }
  if (
    !Array.isArray(envelope.members) ||
    envelope.members.length < 1 ||
    envelope.members.length > 4
  )
    throw new PlatformApiError("续局真人成员响应无效", "INVALID_RESPONSE");
  for (const candidate of envelope.members)
    requireExactObjectKeys(
      candidate,
      ["connected", "nickname", "ready", "role", "seat", "userId"],
      "续局真人成员字段无效",
    );
  requireExactObjectKeys(
    envelope.member,
    ["connected", "nickname", "ready", "role", "seat", "userId"],
    "续局发起者成员字段无效",
  );
  const members = envelope.members.map((member) => parseMember(member));
  const member = parseMember(envelope.member);
  if (
    new Set(members.map((candidate) => candidate.userId)).size !==
      members.length ||
    new Set(members.map((candidate) => candidate.seat)).size !==
      members.length ||
    members.some((candidate) => candidate.ready) ||
    members.filter((candidate) => candidate.role === "owner").length !== 1 ||
    !members.some(
      (candidate) =>
        candidate.userId === room.ownerId && candidate.role === "owner",
    ) ||
    member.userId !== selfUserId ||
    !member.connected ||
    !members.some(
      (candidate) =>
        candidate.userId === member.userId &&
        candidate.seat === member.seat &&
        candidate.role === member.role,
    )
  ) {
    throw new PlatformApiError(
      "续局真人成员与原班语义不一致",
      "INVALID_RESPONSE",
    );
  }
  const inviteToken = requireString(envelope.inviteToken, "续局邀请令牌");
  const qrData = parseInviteQrData(envelope.qrData, room.code, inviteToken);
  if (
    envelope.inviteUrl !== qrData ||
    envelope.invitePath !==
      `/join/${room.code}?invite=${encodeURIComponent(inviteToken)}`
  ) {
    throw new PlatformApiError(
      "续局邀请地址与房间签名不一致",
      "INVALID_RESPONSE",
    );
  }
  return { room, inviteToken, qrData };
}

function requireExactObjectKeys(
  value: unknown,
  keys: readonly string[],
  message: string,
): asserts value is Record<string, unknown> {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).sort().join("\0") !== [...keys].sort().join("\0")
  ) {
    throw new PlatformApiError(message, "INVALID_RESPONSE");
  }
}

function parseRoom(
  value: RemoteRoomRecord,
): Omit<
  LobbyRoom,
  | "members"
  | "players"
  | "selfMember"
  | "version"
  | "phase"
  | "inviteToken"
  | "qrData"
> {
  const status = value.status;
  if (
    status !== "waiting" &&
    status !== "playing" &&
    status !== "settled" &&
    status !== "closed"
  )
    throw new PlatformApiError("房间状态无效", "INVALID_RESPONSE");
  return {
    id: requireString(value.id, "房间 ID"),
    code: requireString(value.code, "房间号"),
    ownerId: requireString(value.ownerId, "房主 ID"),
    status,
    settings: RoomSettingsSchema.parse(value.settings),
  };
}

function parseMember(value: unknown): RoomMemberView {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new PlatformApiError("房间成员响应无效", "INVALID_RESPONSE");
  const member = value as RemoteMember;
  if (!Number.isInteger(member.seat) || member.seat < 0 || member.seat > 3)
    throw new PlatformApiError("房间座位无效", "INVALID_RESPONSE");
  if (member.role !== "owner" && member.role !== "player")
    throw new PlatformApiError("房间成员角色无效", "INVALID_RESPONSE");
  if (
    typeof member.ready !== "boolean" ||
    typeof member.connected !== "boolean"
  )
    throw new PlatformApiError("房间成员状态无效", "INVALID_RESPONSE");
  return {
    userId: requireString(member.userId, "用户 ID"),
    nickname: requireString(member.nickname, "昵称"),
    seat: member.seat as 0 | 1 | 2 | 3,
    role: member.role,
    ready: member.ready,
    connected: member.connected,
  };
}

function parsePublicRoom(value: unknown): PublicRoomView {
  if (!value || typeof value !== "object")
    throw new PlatformApiError("公开房间响应无效", "INVALID_RESPONSE");
  const room = value as Record<string, unknown>;
  const keys = Object.keys(room);
  const expectedKeys = [
    "id",
    "code",
    "name",
    "status",
    "humanCount",
    "capacity",
    "passwordProtected",
    "allowBots",
    "allowSpectators",
    "botDifficulty",
    "createdAt",
  ];
  if (
    keys.length !== expectedKeys.length ||
    !keys.every((key) => expectedKeys.includes(key))
  )
    throw new PlatformApiError("公开房间响应字段无效", "INVALID_RESPONSE");
  const status = room.status;
  const difficulty = room.botDifficulty;
  if (
    status !== "waiting" &&
    status !== "playing" &&
    status !== "settled" &&
    status !== "closed"
  )
    throw new PlatformApiError("公开房间状态无效", "INVALID_RESPONSE");
  if (
    difficulty !== "standard" &&
    difficulty !== "expert" &&
    difficulty !== "master"
  )
    throw new PlatformApiError("AI 难度无效", "INVALID_RESPONSE");
  const humanCount = requireNumber(room.humanCount, "真人数量");
  if (
    !Number.isInteger(humanCount) ||
    humanCount < 0 ||
    humanCount > 4 ||
    room.capacity !== 4 ||
    typeof room.passwordProtected !== "boolean" ||
    typeof room.allowBots !== "boolean" ||
    typeof room.allowSpectators !== "boolean"
  )
    throw new PlatformApiError("公开房间人数或配置无效", "INVALID_RESPONSE");
  return {
    id: requireString(room.id, "房间 ID"),
    code: requireString(room.code, "房间号"),
    name: requireString(room.name, "房间名称"),
    status,
    humanCount,
    capacity: 4,
    passwordProtected: room.passwordProtected,
    allowBots: room.allowBots,
    allowSpectators: room.allowSpectators,
    botDifficulty: difficulty,
    createdAt: requireNumber(room.createdAt, "创建时间"),
  };
}

function apiToWebSocketUrl(baseUrl: string): string {
  const parsed = new URL(baseUrl);
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:")
    throw new PlatformApiError("游戏服务地址无效", "INVALID_CONFIGURATION");
  parsed.protocol = parsed.protocol === "https:" ? "wss:" : "ws:";
  return parsed.toString().replace(/\/$/u, "");
}

function parseMatchmaking(value: unknown): MatchmakingView {
  if (!value || typeof value !== "object")
    throw new PlatformApiError("匹配状态响应无效", "INVALID_RESPONSE");
  const entry = value as Record<string, unknown>;
  const status = entry.status;
  if (
    status !== "queued" &&
    status !== "matched" &&
    status !== "cancelled" &&
    status !== "expired"
  )
    throw new PlatformApiError("匹配状态无效", "INVALID_RESPONSE");
  return {
    id: requireString(entry.id, "匹配 ID"),
    status,
    queuedAt: requireNumber(entry.queuedAt, "排队时间"),
    matchedRoomId:
      entry.matchedRoomId === null
        ? null
        : requireString(entry.matchedRoomId, "匹配房间 ID"),
    searchRange: requireNumber(entry.searchRange, "搜索范围"),
    humanPriorityRemainingMs: requireNumber(
      entry.humanPriorityRemainingMs,
      "真人优先时间",
    ),
    timeoutRemainingMs: requireNumber(entry.timeoutRemainingMs, "超时时间"),
  };
}

async function responseError(response: Response): Promise<PlatformApiError> {
  let body: ApiErrorBody = {};
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    /* response without JSON */
  }
  const code =
    typeof body.error?.code === "string"
      ? body.error.code
      : `HTTP_${response.status}`;
  const message =
    typeof body.error?.message === "string"
      ? body.error.message
      : `游戏服务返回 ${response.status}`;
  const retryAfter =
    typeof body.error?.retryAfterSeconds === "number"
      ? body.error.retryAfterSeconds
      : null;
  return new PlatformApiError(message, code, response.status, retryAfter);
}

function retryableFailure(error: unknown): boolean {
  return (
    isAbortError(error) ||
    !(error instanceof PlatformApiError) ||
    error.status >= 500
  );
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function requireString(value: unknown, label: string): string {
  if (typeof value !== "string" || value.length === 0)
    throw new PlatformApiError(`${label}无效`, "INVALID_RESPONSE");
  return value;
}

function requireNumber(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value))
    throw new PlatformApiError(`${label}无效`, "INVALID_RESPONSE");
  return value;
}

async function localPasswordDigest(
  code: string,
  password: string,
): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(`${code}:${password}`),
  );
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}
